import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  X, 
  Send, 
  Sparkles, 
  Wand2, 
  Lightbulb,
  Box,
  Printer,
  Image,
  Mic,
  Paperclip,
  RotateCcw,
  Copy,
  ThumbsUp,
  ThumbsDown,
  Loader2
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import { textTo3DService } from '@/lib/textTo3D'
import { convertScadToStl } from '@/lib/scadConverter'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, PerspectiveCamera, Center, Environment } from '@react-three/drei'
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js'
import * as THREE from 'three'

interface AICopilotProps {
  onClose: () => void
  onInsertModel?: (code: string, name: string) => void
}

function STLViewer({ stlUrl }: { stlUrl: string }) {
  const [geometry, setGeometry] = useState<THREE.BufferGeometry | null>(null)

  useEffect(() => {
    if (!stlUrl) return
    const loader = new STLLoader()
    const base64Data = stlUrl.split(',')[1] || ''
    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const geom = loader.parse(bytes.buffer)
    geom.center()
    geom.computeVertexNormals()
    setGeometry(geom)
  }, [stlUrl])

  if (!geometry) return null

  return (
    <mesh geometry={geometry} castShadow receiveShadow>
      <meshStandardMaterial color="#8b5cf6" metalness={0.3} roughness={0.4} />
    </mesh>
  )
}

function MiniModelScene({ stlUrl }: { stlUrl: string }) {
  return (
    <>
      <PerspectiveCamera makeDefault position={[30, 30, 30]} fov={50} />
      <OrbitControls enableDamping dampingFactor={0.05} />
      <ambientLight intensity={0.4} />
      <directionalLight position={[30, 30, 15]} intensity={1} />
      <Center>
        <STLViewer stlUrl={stlUrl} />
      </Center>
      <Environment preset="studio" />
    </>
  )
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  type?: 'text' | 'generation' | 'suggestion' | 'error'
  generatedModel?: {
    name: string
    preview: string | null
    code?: string
  }
}

const suggestions = [
  { icon: Box, label: 'Generate a phone stand', category: 'create' },
  { icon: Wand2, label: 'Make this 20% larger', category: 'modify' },
  { icon: Printer, label: 'Optimize for printing', category: 'print' },
  { icon: Lightbulb, label: 'Suggest improvements', category: 'analyze' },
]

const initialMessages: Message[] = [
  {
    id: '1',
    role: 'assistant',
    content: "Hi! I'm your AI design copilot. I can help you create 3D models from text, modify existing designs, optimize for printing, and answer questions. What would you like to create today?",
    timestamp: new Date(),
    type: 'text',
  },
]

export default function AICopilot({ onClose, onInsertModel }: AICopilotProps) {
  const [messages, setMessages] = useState<Message[]>(initialMessages)
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [convertingIds, setConvertingIds] = useState<Set<string>>(new Set())
  const [stlUrls, setStlUrls] = useState<Record<string, string>>({})
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Convert SCAD to STL when a new generation message arrives
  useEffect(() => {
    messages.forEach(async (msg) => {
      console.log('[AICopilot] Checking message:', msg.id, 'type:', msg.type, 'hasCode:', !!msg.generatedModel?.code, 'codeLength:', msg.generatedModel?.code?.length)
      if (
        msg.type === 'generation' &&
        msg.generatedModel?.code &&
        !stlUrls[msg.id] &&
        !convertingIds.has(msg.id)
      ) {
        setConvertingIds((prev) => new Set(prev).add(msg.id))
        try {
          console.log('[AICopilot] Converting SCAD for message:', msg.id, 'code preview:', msg.generatedModel.code.substring(0, 100))
          const result = await convertScadToStl(msg.generatedModel.code)
          if (result.success && result.stlUrl) {
            console.log('[AICopilot] Conversion successful for:', msg.id)
            setStlUrls((prev) => ({ ...prev, [msg.id]: result.stlUrl! }))
          } else {
            console.error('[AICopilot] Conversion failed:', result.error)
          }
        } catch (err) {
          console.error('[AICopilot] Conversion error:', err)
        } finally {
          setConvertingIds((prev) => {
            const next = new Set(prev)
            next.delete(msg.id)
            return next
          })
        }
      }
    })
  }, [messages, stlUrls, convertingIds])

  const handleInsertToCanvas = (code: string, name: string) => {
    if (onInsertModel) {
      onInsertModel(code, name)
      onClose()
    } else {
      console.warn('[AICopilot] No onInsertModel handler provided')
    }
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSend = async () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
      type: 'text',
    }

    setMessages((prev) => [...prev, userMessage])
    const userInput = input
    setInput('')
    setIsTyping(true)

    const isGenerationRequest = userInput.toLowerCase().includes('create') || 
                                 userInput.toLowerCase().includes('generate') ||
                                 userInput.toLowerCase().includes('make') ||
                                 userInput.toLowerCase().includes('design') ||
                                 userInput.toLowerCase().includes('model')

    try {
      if (isGenerationRequest) {
        // Use Claude to generate 3D model code
        const result = await textTo3DService.generate({
          prompt: userInput,
          style: 'realistic',
          complexity: 'medium',
        })

        console.log('[AICopilot] Generation result:', result)
        console.log('[AICopilot] Generated code:', (result as any)._generatedCode?.substring(0, 200))

        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: "I've generated a 3D model based on your description. The model is parametric OpenSCAD code that can be compiled to STL for printing.",
          timestamp: new Date(),
          type: 'generation',
          generatedModel: {
            name: userInput.slice(0, 30) + (userInput.length > 30 ? '...' : ''),
            preview: null,
            code: (result as any)._generatedCode,
          },
        }
        setMessages((prev) => [...prev, assistantMessage])
      } else {
        // General chat via Edge Function
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const response = await fetch(`${supabaseUrl}/functions/v1/ai-chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ message: userInput }),
        })

        if (!response.ok) {
          throw new Error('Failed to get response')
        }

        const data = await response.json()
        if (!data.success) {
          throw new Error(data.error || 'Failed to get response')
        }
        
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.message || "I'm sorry, I couldn't process that request.",
          timestamp: new Date(),
          type: 'text',
        }
        setMessages((prev) => [...prev, assistantMessage])
      }
    } catch (error) {
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        timestamp: new Date(),
        type: 'error',
      }
      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsTyping(false)
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion)
  }

  return (
    <div className="h-full flex flex-col bg-surface-900">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ai-500 to-primary-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-white">AI Copilot</h3>
            <p className="text-xs text-ai-400">Powered by Claude</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-surface-800 transition-colors"
        >
          <X className="w-5 h-5 text-surface-400" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence>
          {messages.map((message) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={cn(
                'flex gap-3',
                message.role === 'user' && 'flex-row-reverse'
              )}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ai-500 to-primary-500 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
              )}
              
              <div className={cn(
                'max-w-[85%] rounded-2xl px-4 py-3',
                message.role === 'user' 
                  ? 'bg-primary-600 text-white rounded-br-md' 
                  : 'bg-surface-800 text-surface-200 rounded-bl-md'
              )}>
                <p className="text-sm leading-relaxed">{message.content}</p>
                
                {message.type === 'generation' && message.generatedModel && (
                  <div className="mt-3 p-3 bg-surface-900/50 rounded-xl">
                    <div className="aspect-video bg-surface-800 rounded-lg flex items-center justify-center mb-3 overflow-hidden">
                      {convertingIds.has(message.id) ? (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="w-8 h-8 text-ai-400 animate-spin" />
                          <span className="text-xs text-surface-400">Converting...</span>
                        </div>
                      ) : stlUrls[message.id] ? (
                        <Canvas className="w-full h-full">
                          <MiniModelScene stlUrl={stlUrls[message.id]!} />
                        </Canvas>
                      ) : (
                        <Box className="w-12 h-12 text-ai-400" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-white">{message.generatedModel.name}</span>
                      <div className="flex items-center gap-1">
                        <button 
                          className="p-1.5 rounded-lg hover:bg-surface-700 transition-colors"
                          onClick={() => {
                            if (message.generatedModel?.code) {
                              navigator.clipboard.writeText(message.generatedModel.code)
                            }
                          }}
                          title="Copy SCAD code"
                        >
                          <Copy className="w-4 h-4 text-surface-400" />
                        </button>
                        <button className="p-1.5 rounded-lg hover:bg-surface-700 transition-colors">
                          <RotateCcw className="w-4 h-4 text-surface-400" />
                        </button>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button 
                        variant="ai" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => handleInsertToCanvas(
                          message.generatedModel!.code || '', 
                          message.generatedModel!.name
                        )}
                        disabled={!message.generatedModel.code}
                      >
                        Insert to Canvas
                      </Button>
                      <Button variant="secondary" size="sm">
                        Refine
                      </Button>
                    </div>
                  </div>
                )}

                {message.role === 'assistant' && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-surface-700">
                    <button className="p-1 rounded hover:bg-surface-700 transition-colors">
                      <ThumbsUp className="w-3.5 h-3.5 text-surface-500 hover:text-success-400" />
                    </button>
                    <button className="p-1 rounded hover:bg-surface-700 transition-colors">
                      <ThumbsDown className="w-3.5 h-3.5 text-surface-500 hover:text-error-400" />
                    </button>
                    <button className="p-1 rounded hover:bg-surface-700 transition-colors ml-auto">
                      <Copy className="w-3.5 h-3.5 text-surface-500" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isTyping && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-ai-500 to-primary-500 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="bg-surface-800 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-ai-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-ai-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-ai-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <p className="text-xs text-surface-500 mb-2">Try asking:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.label}
                onClick={() => handleSuggestionClick(suggestion.label)}
                className="flex items-center gap-2 px-3 py-1.5 bg-surface-800 hover:bg-surface-700 rounded-full text-xs text-surface-300 transition-colors"
              >
                <suggestion.icon className="w-3 h-3 text-ai-400" />
                {suggestion.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-surface-800">
        <div className="flex items-end gap-2">
          <div className="flex-1 bg-surface-800 rounded-xl border border-surface-700 focus-within:border-ai-500 transition-colors">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              placeholder="Describe what you want to create..."
              rows={1}
              className="w-full bg-transparent px-4 py-3 text-sm text-white placeholder:text-surface-500 resize-none focus:outline-none"
            />
            <div className="flex items-center gap-1 px-2 pb-2">
              <button className="p-2 rounded-lg hover:bg-surface-700 transition-colors">
                <Paperclip className="w-4 h-4 text-surface-400" />
              </button>
              <button className="p-2 rounded-lg hover:bg-surface-700 transition-colors">
                <Image className="w-4 h-4 text-surface-400" />
              </button>
              <button className="p-2 rounded-lg hover:bg-surface-700 transition-colors">
                <Mic className="w-4 h-4 text-surface-400" />
              </button>
            </div>
          </div>
          <Button
            variant="ai"
            size="icon"
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
